import { Link } from "react-router";
import { useAiUsage, type AiUsage } from "../../lib/admin";
import { fmtDecimal, fmtNumber, fmtPercent } from "../../lib/format";
import { BarList } from "../../components/charts/small";
import { ChartLegend, LineChart } from "../../components/charts/LineChart";
import { StatTile } from "../../components/stats";
import { Ago, Badge, Callout, Card, CardEmpty, CardHeader, Empty, ErrorState, None, PageHeader, Skeleton, StatGrid, Table, TableScroll } from "../../components/ui";
import { usePageTitle } from "./components";
import { StepFunnel } from "../../components/charts/small";

const fmtMs = (ms: number) => (ms < 1000 ? `${fmtNumber(ms)} ms` : `${fmtDecimal(ms / 1000)} s`);
const rate = (failed: number, calls: number) => (calls ? failed / calls : 0);

export function AdminAi() {
  const q = useAiUsage();
  const d = q.data;
  usePageTitle("AI assistants");

  if (q.isError) return <ErrorState error={q.error} />;

  return (
    <>
      <PageHeader title="AI assistants" subtitle="Who has connected Claude or another MCP client, and what they use it for. Tool calls cover the last 30 days." />
      <div className="space-y-4" aria-busy={q.isPending}>
        <Card>
          <StatGrid loading={!d}>
            {d && (
              <>
                <StatTile label="Connected" value={fmtNumber(d.totals.connectedUsers)} hint="People with at least one live API token" />
                <StatTile label="Active, 7 days" value={fmtNumber(d.totals.activeUsers7d)} current={d.totals.activeUsers7d} previous={d.totals.activeUsersPrev7d} hint="People whose token was used this week" />
                <StatTile label="Tool calls, 7 days" value={fmtNumber(d.totals.calls7d)} current={d.totals.calls7d} previous={d.totals.callsPrev7d} />
                <StatTile label="Failed calls, 30 days" value={fmtPercent(rate(d.totals.failed30d, d.totals.calls30d))} hint={`${fmtNumber(d.totals.failed30d)} of ${fmtNumber(d.totals.calls30d)} calls returned an error`} />
                <StatTile label="New tokens, 7 days" value={fmtNumber(d.totals.newTokens7d)} current={d.totals.newTokens7d} previous={d.totals.newTokensPrev7d} />
                <StatTile label="Can draft" value={fmtNumber(d.totals.writeTokens)} hint={`Live tokens with read + draft access, of ${d.totals.activeTokens}. ${d.totals.revoked30d} revoked in 30 days.`} />
              </>
            )}
          </StatGrid>
        </Card>

        {d && !d.tracking && (
          <Callout role="note">Tool calls are recorded only on app.metrickle.com, so here you'll see connections but no calls.</Callout>
        )}

        <div className="grid gap-4 @4xl:grid-cols-2">
          <StepFunnel title="AI adoption" subtitle="People at each step. Revoked tokens still count as having connected." steps={d?.funnel} />
          <CallsCard d={d} />
        </div>

        <div className="grid gap-4 @4xl:grid-cols-3">
          <Card className="min-w-0 @4xl:col-span-2">
            <CardHeader title="Tools" subtitle="Which tools assistants call, and how often they fail" />
            <ToolsTable d={d} />
          </Card>
          <Card>
            <CardHeader title="Clients" subtitle="From the assistant's User-Agent" />
            <div className="pb-3">
              {!d ? (
                <Skeleton className="mx-4 h-24" />
              ) : d.clients.length ? (
                <BarList
                  aria-label="Tool calls by client"
                  valueLabel="Calls"
                  rows={d.clients.map((c) => ({ key: c.client, label: c.client, value: c.calls, secondary: `${c.users} ${c.users === 1 ? "person" : "people"}` }))}
                />
              ) : (
                <CardEmpty>No tool calls yet.</CardEmpty>
              )}
            </div>
          </Card>
        </div>

        <Card>
          <CardHeader title="People" subtitle="Everyone who has created an API token, most active first" />
          <PeopleTable d={d} />
        </Card>
      </div>
    </>
  );
}

function CallsCard({ d }: { d: AiUsage | undefined }) {
  const series = [
    { id: "calls", label: "Calls", color: "var(--series-1)", values: d?.series.map((p) => p.calls) ?? [] },
    { id: "failed", label: "Failed", color: "var(--bad)", values: d?.series.map((p) => p.failed) ?? [] },
  ];
  return (
    <Card>
      <CardHeader title="Tool calls per day" subtitle="All calls, and the ones that returned an error (UTC days)" />
      <div className="px-2 pb-3">
        {d ? (
          <LineChart
            height={200}
            labels={d.series.map((p) => new Date(p.t).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" }))}
            series={series}
            format={fmtNumber}
            table={{ caption: "Tool calls and failed calls per day (UTC), oldest first", rowHeader: "Day" }}
          />
        ) : (
          <Skeleton className="mx-2 h-[200px]" />
        )}
      </div>
      <ChartLegend series={series} />
    </Card>
  );
}

function ToolsTable({ d }: { d: AiUsage | undefined }) {
  if (!d) return <Skeleton className="m-4 h-32" />;
  if (!d.tools.length) return <CardEmpty divided>No tool calls yet.</CardEmpty>;
  return (
    <TableScroll label="Tools" divided>
      <Table caption="Tool calls in the last 30 days, by tool" className="min-w-[560px]">
        <thead>
          <tr>
            <th scope="col">Tool</th>
            <th scope="col" className="text-right">Calls</th>
            <th scope="col" className="text-right">People</th>
            <th scope="col" className="text-right">Failed</th>
            <th scope="col" className="text-right">Avg time</th>
          </tr>
        </thead>
        <tbody className="tabular">
          {d.tools.map((t) => (
            <tr key={t.tool}>
              <th scope="row" className="font-mono text-[13px] text-ink">{t.tool}</th>
              <td className="text-right text-ink">{fmtNumber(t.calls)}</td>
              <td className="text-right text-ink-2">{fmtNumber(t.users)}</td>
              <td className={t.failed ? "text-right text-bad" : "text-right text-ink-3"}>
                {fmtPercent(rate(t.failed, t.calls), 0)}
                <span className="sr-only"> ({t.failed} calls)</span>
              </td>
              <td className="text-right text-ink-2">{fmtMs(t.avgMs)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
}

function PeopleTable({ d }: { d: AiUsage | undefined }) {
  if (!d) return <Skeleton className="m-4 h-32" />;
  if (!d.users.length) return <Empty title="No one has connected an assistant yet">People create tokens under Connect AI in the dashboard.</Empty>;
  return (
    <TableScroll label="People with an API token" divided>
      <Table caption="People who have created an API token. Select a name to open their record." className="min-w-[860px]">
        <thead>
          <tr>
            <th scope="col">Person</th>
            <th scope="col">Access</th>
            <th scope="col">Connected</th>
            <th scope="col">Last used</th>
            <th scope="col" className="text-right">Calls 30d</th>
            <th scope="col" className="text-right">Failed</th>
            <th scope="col">Top tool</th>
          </tr>
        </thead>
        <tbody>
          {d.users.map((u) => (
            <tr key={u.userId}>
              <th scope="row">
                <span className="flex items-center gap-2">
                  {u.contactId ? (
                    <Link to={`/admin/people/${u.contactId}`} className="font-medium text-ink hover:underline">{u.name || u.email}</Link>
                  ) : (
                    <span className="font-medium text-ink">{u.name || u.email}</span>
                  )}
                  {u.platformTeam && <Badge>Platform team</Badge>}
                </span>
                <span className="block text-xs text-ink-3">{u.email}</span>
              </th>
              <td>
                {u.scope === null ? (
                  <Badge tone="bad">Revoked</Badge>
                ) : (
                  <span className="flex items-center gap-2 whitespace-nowrap">
                    <Badge tone={u.scope === "write" ? "accent" : "neutral"}>{u.scope === "write" ? "Read + draft" : "Read"}</Badge>
                    {u.activeTokens > 1 && <span className="text-xs text-ink-3">{u.activeTokens} tokens</span>}
                  </span>
                )}
              </td>
              <td className="text-ink-2"><Ago t={u.connectedAt} /></td>
              <td className="text-ink-2"><Ago t={u.lastUsedAt} /></td>
              <td className="text-right tabular text-ink">{fmtNumber(u.calls30d)}</td>
              <td className={u.failed30d ? "text-right tabular text-bad" : "text-right tabular text-ink-3"}>{fmtNumber(u.failed30d)}</td>
              <td className="font-mono text-[13px] text-ink-2">{u.topTool ?? <None />}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
}
